/**
 * Turn-advance resolution — everything that happens between one player's turn
 * ending and the next player's aim phase, as one pure function. Previously
 * inline in Game.nextTurn (main.ts) mixed with mesh/anim/UI updates.
 *
 *   advanceTurn → stale commands → fire zones → EMP/tether decay
 *   → (on round wrap) CTF respawn, mode respawns, interaction ticks +
 *     interruptions, Signal Trace extraction, Excavation drills, ruin claims,
 *     Blackout, Last Light, match events
 *   → territory scores → volcanic eruptions
 *
 * Early exits (objective win, eruption kill) stop processing exactly where the
 * original code returned, and set `gameOver`.
 */

import type { GameState, PlayerState, RuinState } from '../types';
import { advanceTurn, isGameOver, getHazardWorldPosition } from './gameState';
import { cleanupStaleCommands } from './botCommands';
import { pruneFogMarkers } from './fog';
import { GHOST_MARKER_MAX_AGE } from '../constants';
import { processFireZones, processEmpDecay, processTetherDecay, killUnit } from './damage';
import { tickCooldowns, canRespawn, executeRespawn } from './respawn';
import { tickInteraction, checkInteractionInterruptions } from './interactions';
import { startExtraction, tickExtraction, isInExtractionZone } from './extraction';
import { tickAllDrillSites, checkDrillExtraction, createArtifactCarryable } from './excavation';
import { tickClaims, processClaimContestations, applyClaimBenefits } from './claims';
import { tickBlackout, getBlackoutWarnings } from './blackout';
import { tickAlert, observe, noticeEvidence, describeChange, tickProgress } from './perception';
import { tickLastLight, getDestructionWarnings, destroyPlanet } from './lastLight';
import { tickMatchEvents, generateEventNotification } from './matchEvents';
import { computeTerritoryScores } from './stakes';
import { processVolcanicEruptions } from './hazardEffects';
import { addActivatedPost, checkTriangulation, revealSignal } from './clues';
import { SeededRandom } from './random';
import type { Vec3 } from '../types';

// =============================================================================
// Interaction activation (listening posts, data vaults)
// =============================================================================

export interface ActivationResult {
  state: GameState;
  logs: string[];
  /** The team just triangulated the signal — show the source marker. */
  signalRevealed: boolean;
}

export function activateInteraction(state: GameState, player: PlayerState, ruin: RuinState): ActivationResult {
  const logs: string[] = [];
  const ms = state.modeState;
  const st = ms.signalTrace;

  if (st) {
    const rng = new SeededRandom(`${state.config.seed}-clue-${ruin.id}-${player.teamIndex}`);
    const newClueState = addActivatedPost(
      st.clueState, player.teamIndex, ruin.id, ruin.planetIndex, st.signalPlanetIndex, state.planets, rng,
    );
    let next: GameState = { ...state, modeState: { ...ms, signalTrace: { ...st, clueState: newClueState } } };

    const teamClues = newClueState.clues[player.teamIndex] ?? [];
    const latest = teamClues[teamClues.length - 1];
    if (latest) logs.push(`Clue received: ${latest.description}`);

    const postsRequired = state.config.modeConfig.postsToTriangulate ?? 3;
    if (checkTriangulation(newClueState, player.teamIndex, postsRequired, state.ruins)) {
      const revealed = revealSignal(newClueState, player.teamIndex);
      next = { ...next, modeState: { ...ms, signalTrace: { ...st, clueState: revealed } } };
      logs.push(`Signal triangulated! Source located on planet ${st.signalPlanetIndex}. Go there to extract!`);
      return { state: next, logs, signalRevealed: true };
    }
    return { state: next, logs, signalRevealed: false };
  }

  if (ms.uplink) {
    logs.push(`${player.name} activates data vault!`);
  }
  return { state, logs, signalRevealed: false };
}

// =============================================================================
// Turn start
// =============================================================================

export interface TurnResolution {
  state: GameState;
  logs: string[];
  /** Players brought back this turn (reset their animation state). */
  respawned: number[];
  /** Excavation artifact carryable created this turn (needs a mesh). */
  artifactCreatedId: number | null;
  /** Planets destroyed by Last Light this turn (sync WASM alive flags). */
  destroyedPlanets: number[];
  /** Volcanic hazards that erupted (effects + hazard animation). */
  erupted: Array<{ hazardId: number; position: Vec3; surfaceDir: Vec3 }>;
  /** Eruption damage per player (combat cues). */
  eruptionDamages: Array<{ playerId: number; isBot: boolean }>;
  /** Players launched into the air by eruptions. */
  launched: number[];
  /** Signal Trace extraction finished this turn (remove the source marker). */
  extractionCompleted: boolean;
  /** A team triangulated the signal through an auto-ticked interaction (show the marker). */
  signalRevealed: boolean;
  /** Processing stopped on a game-over condition. */
  gameOver: boolean;
}

export function resolveTurnStart(input: GameState): TurnResolution {
  const logs: string[] = [];
  const respawned: number[] = [];
  const destroyedPlanets: number[] = [];
  const erupted: TurnResolution['erupted'] = [];
  const eruptionDamages: TurnResolution['eruptionDamages'] = [];
  const launched: number[] = [];
  let artifactCreatedId: number | null = null;
  let extractionCompleted = false;
  let signalRevealed = false;

  const done = (state: GameState, gameOver: boolean): TurnResolution => ({
    state, logs, respawned, artifactCreatedId, destroyedPlanets, erupted, eruptionDamages, launched,
    extractionCompleted, signalRevealed, gameOver,
  });

  const prevTurn = input.turnNumber;
  let s = advanceTurn(input);
  s = cleanupStaleCommands(s);

  // Fog bookkeeping: ghosts of the dead, of destroyed structures and of
  // long-unseen enemies fade out of every player's map
  if (s.fog.length > 0) {
    const fog = s.fog.map(f => pruneFogMarkers(f, s.players, s.structures, s.turnNumber, GHOST_MARKER_MAX_AGE));
    if (fog.some((f, i) => f !== s.fog[i])) s = { ...s, fog };
  }

  const roundWrapped = s.turnNumber > prevTurn;
  const endedPlayerId = input.players[input.currentPlayerIndex]?.id;
  const startingPlayerId = s.players[s.currentPlayerIndex]?.id;

  // Fire zones: burn the player whose turn is starting if they stand in one;
  // zones burn out once per round, not once per player turn
  if (s.fireZones && s.fireZones.length > 0) {
    const fire = processFireZones(s.fireZones, s.players, s.planets, {
      tick: roundWrapped,
      burnPlayerIds: startingPlayerId !== undefined ? [startingPlayerId] : [],
    });
    for (const d of fire.damages) {
      const p = fire.players.find(pl => pl.id === d.playerId);
      if (p) logs.push(`${p.name} takes ${Math.round(d.damage)} fire dmg`);
    }
    s = { ...s, players: fire.players, fireZones: fire.fireZones };
  }

  // Status effects count the victim's own turns: decay the player who just finished
  if (endedPlayerId !== undefined) {
    s = { ...s, players: processEmpDecay(s.players, endedPlayerId) };
    s = { ...s, players: processTetherDecay(s.players, endedPlayerId) };
  }

  if (roundWrapped) {
    const ms = s.modeState;

    // Respawn layer: cooldowns tick once per round, then anyone ready comes back
    if (ms.respawn) {
      const respawn = tickCooldowns(ms.respawn);
      s = { ...s, modeState: { ...ms, respawn } };
      for (const p of s.players) {
        if (!p.alive && canRespawn(p.id, p.teamIndex, respawn)) {
          s = executeRespawn(s, p.id);
          const atHome = s.config.modeConfig.homeTeamPlanets?.[p.teamIndex] !== undefined;
          logs.push(atHome ? `${p.name} respawns at home base!` : `${p.name} respawns!`);
          respawned.push(p.id);
        }
      }
    }

    // Auto-tick active interactions (before interruption check, so completed
    // interactions aren't cancelled because the player walked away after planting)
    {
      const cur = s.modeState;
      if (cur.interactionProgress && cur.interactionProgress.length > 0) {
        let progress = [...cur.interactionProgress];
        for (const entry of [...cur.interactionProgress]) {
          const result = tickInteraction(progress, entry.playerId, entry.ruinId);
          progress = result.progress;
          if (result.activated) {
            const player = s.players.find(p => p.id === entry.playerId);
            const ruin = s.ruins.find(r => r.id === entry.ruinId);
            if (player && ruin) {
              logs.push(`${player.name}'s interaction complete!`);
              s = { ...s, modeState: { ...s.modeState, interactionProgress: progress } };
              const act = activateInteraction(s, player, ruin);
              s = act.state;
              logs.push(...act.logs);
              if (act.signalRevealed) signalRevealed = true;
            }
          }
        }
        s = { ...s, modeState: { ...s.modeState, interactionProgress: progress } };
      }
    }

    // Interaction interruptions (player left range, died, ...)
    {
      const cur = s.modeState;
      if (cur.interactionProgress && cur.interactionProgress.length > 0) {
        const cleaned = checkInteractionInterruptions(cur.interactionProgress, s.players, s.ruins, s.planets);
        if (cleaned.length !== cur.interactionProgress.length) {
          s = { ...s, modeState: { ...cur, interactionProgress: cleaned } };
        }
      }
    }

    // Signal Trace: auto-start + tick extraction
    {
      const st = s.modeState.signalTrace;
      if (st) {
        if (!st.extraction) {
          const target = { planetIndex: st.signalPlanetIndex, surfaceDir: st.signalSurfaceDir };
          for (const p of s.players) {
            if (!p.alive) continue;
            if (!st.clueState.signalRevealed[p.teamIndex]) continue;
            if (isInExtractionZone(p, target, s.planets)) {
              const turns = s.config.modeConfig.extractionTurns ?? 5;
              s = { ...s, modeState: { ...s.modeState, signalTrace: { ...st, extraction: startExtraction(p.teamIndex, target, turns) } } };
              logs.push(`${p.name} begins signal extraction! Hold position for ${turns} turns.`);
              break;
            }
          }
        }
        const st2 = s.modeState.signalTrace;
        if (st2 && st2.extraction) {
          const ext = tickExtraction(st2.extraction, s.players, s.planets);
          s = {
            ...s,
            modeState: {
              ...s.modeState,
              signalTrace: {
                ...st2,
                extraction: ext.extraction,
                extractionCompletedTeam: ext.completed
                  ? (st2.extraction?.teamIndex ?? st2.extractionCompletedTeam ?? null)
                  : (st2.extractionCompletedTeam ?? null),
              },
            },
          };
          if (ext.completed) {
            logs.push('Signal source extracted! Objective complete!');
            extractionCompleted = true;
            if (isGameOver(s)) return done(s, true);
          } else if (ext.extraction && ext.extraction.turnsHeld > 0) {
            logs.push(`Extraction: ${ext.extraction.turnsHeld}/${ext.extraction.turnsRequired} turns held`);
          }
        }
      }
    }

    // Excavation: drill sites
    if (ms.excavation) {
      s = tickAllDrillSites(s);
      const { extracted, teamIndex } = checkDrillExtraction(s);
      if (extracted && teamIndex !== null && !ms.excavation.artifactExtracted) {
        s = createArtifactCarryable(s, teamIndex);
        const artifact = s.carryables[s.carryables.length - 1];
        if (artifact && artifact.type === 'artifact') artifactCreatedId = artifact.id;
        logs.push(`Team ${teamIndex} extracts the artifact!`);
      }
    }

    // Ruin claims + contestations + benefits
    if (s.ruins.length > 0) {
      const ticked = tickClaims(s.ruins, s.players, s.planets);
      const contested = processClaimContestations(ticked, s.players, s.planets);
      s = { ...s, ruins: contested };
      s = applyClaimBenefits(s);
    }

    // Alert layer (Phase 57): levels decay once a round
    if (s.modeState.alert) {
      const t = tickAlert(s, s.turnNumber);
      s = t.state;
      for (const c of t.changes) logs.push(describeChange(c, s.planets));
    }

    // Blackout layer
    if (s.modeState.blackout) {
      const bo = tickBlackout(s.modeState.blackout, s.turnNumber, s.config.modeConfig);
      s = { ...s, modeState: { ...s.modeState, blackout: bo.updatedState } };
      for (const pi of bo.newlyDarkened) if (s.planets[pi]) logs.push(`WARNING: Planet ${pi} has gone dark!`);
      for (const w of getBlackoutWarnings(s.modeState.blackout, s.turnNumber, s.config.modeConfig)) {
        logs.push(`Planet ${w.planetIndex} will go dark in ${w.turnsUntil} turn${w.turnsUntil === 1 ? '' : 's'}`);
      }
    }

    // Last Light layer
    if (s.modeState.lastLight) {
      const ll = tickLastLight(s.modeState.lastLight, s.turnNumber, s.config.modeConfig);
      s = { ...s, modeState: { ...s.modeState, lastLight: ll.updatedState } };
      for (const pi of ll.newlyDestroyed) {
        const planet = s.planets[pi];
        if (planet && planet.alive) {
          const result = destroyPlanet(planet, s.players, pi);
          const planets = [...s.planets];
          planets[pi] = result.updatedPlanet;
          const players = s.players.map(p => (result.killedPlayerIds.includes(p.id) ? killUnit(p, 'planet') : p));
          s = { ...s, planets, players };
          destroyedPlanets.push(pi);
          logs.push(`NOVA: Planet ${pi} has been destroyed!`);
          for (const pid of result.killedPlayerIds) {
            const killed = s.players.find(p => p.id === pid);
            if (killed) logs.push(`${killed.name} perished in the nova!`);
          }
        }
      }
      for (const w of getDestructionWarnings(s.modeState.lastLight, s.turnNumber, s.config.modeConfig)) {
        logs.push(`Planet ${w.planetIndex} will be destroyed in ${w.turnsUntil} turn${w.turnsUntil === 1 ? '' : 's'}!`);
      }
    }

    // Match events
    if (s.matchEvents) {
      const prevEvents = s.matchEvents.events;
      s = tickMatchEvents(s);
      if (s.matchEvents) {
        for (const ev of s.matchEvents.events) {
          const prev = prevEvents.find(e => e.id === ev.id);
          if (prev && !prev.active && ev.active) logs.push(generateEventNotification(ev).message);
        }
      }
    }
  }

  // Territory scores (stakes objective)
  if (s.modeState.territory) {
    const scores = computeTerritoryScores(s);
    const record: Record<number, number> = {};
    for (const [k, v] of scores) record[k] = v;
    s = { ...s, modeState: { ...s.modeState, territory: { ...s.modeState.territory, territoryScores: record } } };
  }

  // Volcanic eruptions at turn start
  if (s.phase !== 'gameover') {
    const er = processVolcanicEruptions(s.players, s.planets, s.hazards, s.config.seed, s.turnNumber);
    if (er.eruptedHazardIds.length > 0) {
      s = { ...s, players: er.players };
      for (const hazardId of er.eruptedHazardIds) {
        const hz = s.hazards.find(h => h.id === hazardId);
        if (hz) {
          logs.push('Volcanic eruption!');
          erupted.push({ hazardId, position: getHazardWorldPosition(hz, s.planets), surfaceDir: hz.surfaceDir });
        }
      }
      for (const d of er.damages) {
        const p = s.players.find(pl => pl.id === d.playerId);
        if (p) {
          logs.push(`${p.name} takes ${Math.round(d.damage)} eruption dmg`);
          eruptionDamages.push({ playerId: p.id, isBot: !!p.isBot });
        }
      }
      for (const kb of er.knockbacks) {
        const p = s.players.find(pl => pl.id === kb.playerId);
        if (p && kb.launched) { logs.push(`${p.name} launched by eruption!`); launched.push(p.id); }
      }
      if (isGameOver(s)) return done(s, true);
    }
  }

  // Alert layer (Phase 57): every guard looks, every turn — and notices what has changed around it (57.7a)
  if (s.modeState.alert) {
    const o = observe(s, s.turnNumber);
    s = o.state;
    for (const c of o.changes) logs.push(describeChange(c, s.planets));
    const e = noticeEvidence(s, s.turnNumber);
    s = e.state;
    for (const c of e.changes) logs.push(describeChange(c, s.planets));
    s = tickProgress(s, s.turnNumber);   // 57.7c: the objective-aware clock's ledger
  }

  return done(s, s.phase === 'gameover');
}
